"use client";

import { apiRequest, UTIL_ENDPOINTS } from "@alba-cars/common-modules";
import qs from "qs";
export interface ClientCompressionOptions {
  quality?: number; // 0-100
  maxWidth?: number;
  maxHeight?: number;
  format?: "jpeg" | "webp" | "png" | "original";
}

export interface CompressionResult {
  file: File;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
  width: number;
  height: number;
}

export const compressImageViaApi = async (
  file: File,
  options: ClientCompressionOptions = {},
): Promise<CompressionResult> => {
  const formData = new FormData();
  formData.append("file", file);
  // formData.append("options", JSON.stringify(options));

  // const response = await fetch("/api/compress-image", {
  //   method: "POST",
  //   body: formData,
  // });

  const queryString = qs.stringify(options);
  const baseUrl = process.env.NEXT_PUBLIC_API_URL;
  let endpoint = `${baseUrl}${UTIL_ENDPOINTS.media.compressImage}`;

  const response = await fetch(`${endpoint}?${queryString}`, {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Compression failed with status ${response.status}`);
  }

  // Get compression metadata from headers
  const originalSize = parseInt(response.headers.get("X-Original-Size") || "0");
  const compressedSize = parseInt(response.headers.get("X-Compressed-Size") || "0");
  const compressionRatio = parseFloat(response.headers.get("X-Compression-Ratio") || "0");
  const width = parseInt(response.headers.get("X-Image-Width") || "0");
  const height = parseInt(response.headers.get("X-Image-Height") || "0");
  const contentType = response.headers.get("Content-Type") || file.type;

  // Get compressed image data
  const compressedBuffer = await response.arrayBuffer();

  // Create new file with compressed data
  const compressedFile = new File([compressedBuffer], file.name, {
    type: contentType,
    lastModified: Date.now(),
  });

  return {
    file: compressedFile,
    originalSize,
    compressedSize,
    compressionRatio,
    width,
    height,
  };
};

// Client-side compression using Canvas (fallback)
export const compressImageClientSide = (file: File, quality: number = 0.8): Promise<File> => {
  return new Promise((resolve, reject) => {
    // Skip compression for non-lossy formats
    const lossyFormats = ["image/jpeg", "image/jpg", "image/webp"];

    if (!lossyFormats.includes(file.type.toLowerCase())) {
      console.log(`Skipping compression for ${file.type} (non-lossy format)`);
      resolve(file);
      return;
    }

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0, img.width, img.height);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Failed to compress image"));
            return;
          }

          const compressedFile = new File([blob], file.name, {
            type: file.type,
            lastModified: Date.now(),
          });

          console.log(
            `Image compressed: ${file.size} bytes -> ${compressedFile.size} bytes (${Math.round((1 - compressedFile.size / file.size) * 100)}% reduction)`,
          );
          resolve(compressedFile);
        },
        file.type,
        quality,
      );

      URL.revokeObjectURL(img.src);
    };

    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error("Failed to load image for compression"));
    };

    img.src = URL.createObjectURL(file);
  });
};
